import {
  RefCallback,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const GUARD = '__noopClickableGuard' as const;

export const TAB_GROUP_CLICKABLE_GUARD_SELECTOR =
  '[role="tablist"], [role="tablist"] *';

type GuardedHTMLElement = HTMLElement & { [GUARD]?: boolean };

type UseNoopClickableGuardOptions = {
  forceSelector?: string;
  hideRedundantSelectLabels?: boolean;
  reprocessOnInteraction?: boolean;
};

const guardOnClick = (el: GuardedHTMLElement) => {
  if (el[GUARD]) return;
  el[GUARD] = true;

  if (el.onclick) el.onclick = null;

  Object.defineProperty(el, 'onclick', {
    configurable: true,
    enumerable: true,
    get: () => null,
    set: () => {},
  });
};

export const guardNoopClickableElement = (el: HTMLElement) => {
  guardOnClick(el);
};

const hideRedundantSelectLabels = (root: ParentNode) => {
  root
    .querySelectorAll<HTMLElement>(
      '[role="combobox"][aria-label][aria-labelledby]',
    )
    .forEach(combo => {
      combo
        .getAttribute('aria-labelledby')
        ?.split(/\s+/)
        .forEach(id => {
          document.getElementById(id)?.setAttribute('aria-hidden', 'true');
        });
    });
};

const shouldGuardElement = (
  el: HTMLElement,
  options?: UseNoopClickableGuardOptions,
) => {
  return (
    !!el.onclick ||
    (!!options?.forceSelector && el.matches(options.forceSelector))
  );
};

const processSubtree = (node: Node, options?: UseNoopClickableGuardOptions) => {
  if (!(node instanceof HTMLElement)) return;

  if (shouldGuardElement(node, options)) guardOnClick(node);

  node.querySelectorAll<HTMLElement>('*').forEach(el => {
    if (shouldGuardElement(el, options)) guardOnClick(el);
  });

  if (options?.hideRedundantSelectLabels) {
    hideRedundantSelectLabels(node);
  }
};

const useNoopClickableGuard = <T extends HTMLElement>(
  options?: UseNoopClickableGuardOptions,
): RefCallback<T> => {
  const [root, setRoot] = useState<T | null>(null);
  const setRootRef = useCallback((node: T | null) => {
    setRoot(node);
  }, []);

  useLayoutEffect(() => {
    if (!root) return undefined;

    const animationFrames: number[] = [];
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const processAfterRender = (node: Node) => {
      processSubtree(node, options);
      animationFrames.push(
        requestAnimationFrame(() => processSubtree(node, options)),
      );
      timeouts.push(setTimeout(() => processSubtree(node, options), 0));
      timeouts.push(setTimeout(() => processSubtree(node, options), 50));
    };

    processAfterRender(root);

    const reprocessRootAfterInteraction = () => {
      animationFrames.push(
        requestAnimationFrame(() => processAfterRender(root)),
      );
    };

    if (options?.reprocessOnInteraction) {
      document.addEventListener('focusin', reprocessRootAfterInteraction, true);
      document.addEventListener('keydown', reprocessRootAfterInteraction, true);
    }

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        processAfterRender(mutation.target);
        mutation.addedNodes.forEach(processAfterRender);
      }
    });
    observer.observe(root, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      if (options?.reprocessOnInteraction) {
        document.removeEventListener(
          'focusin',
          reprocessRootAfterInteraction,
          true,
        );
        document.removeEventListener(
          'keydown',
          reprocessRootAfterInteraction,
          true,
        );
      }
      animationFrames.forEach(cancelAnimationFrame);
      timeouts.forEach(clearTimeout);
    };
  }, [options, root]);

  return setRootRef;
};

export const useTabGroupClickableGuard = <T extends HTMLElement>() => {
  const options = useMemo(
    () => ({
      forceSelector: TAB_GROUP_CLICKABLE_GUARD_SELECTOR,
      reprocessOnInteraction: true,
    }),
    [],
  );

  return useNoopClickableGuard<T>(options);
};

export default useNoopClickableGuard;
